import { useEffect, useMemo, useState, useRef, forwardRef } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { Stack, Text, Spinner, SpinnerSize } from "@fluentui/react";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import rehypeExternalLinks from "rehype-external-links";
import styles from "./Answer.module.css";
import { AskResponse, Citation } from "../../api";
import { parseAnswer } from "./AnswerParser";
import { ChartRenderer } from "./ChartRenderer";
import { TableRenderer } from "./TableRenderer";
import { toPng } from "html-to-image";
import { IconButton, INFO_ICONS, PPT_ICONS, WORD_ICONS } from "../IconButton";

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

interface Props {
  answer: AskResponse;
  onCitationClicked: (citedDocument: Citation) => void;
  onIntentClicked: (intent: string) => void;
  onSpeak?: any;
  isActive?: boolean;
  index: number;
  isFinalAnswer?: boolean;
  showActions?: boolean;
}

const MyStackComponent = forwardRef<HTMLDivElement, any>((props, ref) => (
  <div {...props} ref={ref} />
));

export const Answer = ({
  answer,
  onCitationClicked,
  onIntentClicked,
  index,
  isFinalAnswer = true,
  showActions = true,
}: Props) => {
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] =
    useBoolean(false);
  const filePathTruncationLimit = 50;
  const answerContainerRef = useRef<HTMLDivElement>(null);
  const messageBoxId = "message-" + index;
  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
  const [chevronIsExpanded, setChevronIsExpanded] =
    useState(isRefAccordionOpen);
  const refContainer = useRef<HTMLDivElement>(null);

  const [isExporting, setIsExporting] = useState(false);

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded);
    toggleIsRefAccordionOpen();
  };

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen);
    if (chevronIsExpanded && refContainer.current) {
      refContainer.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chevronIsExpanded, isRefAccordionOpen]);

  const createCitationFilepath = (
    citation: Citation,
    index: number,
    truncate: boolean = false,
  ) => {
    let citationFilename = "";

    if (citation.title && citation.chunk_id != null) {
      if (truncate && citation.title.length > filePathTruncationLimit) {
        const citationLength = citation.title.length;
        citationFilename = `${citation.title.substring(0, 20)}...${citation.title.substring(citationLength - 20)}`;
      } else {
        citationFilename = `${citation.title}`;
      }
    } else if (citation.source_name) {
      citationFilename = citation.source_name
        .replace(/[^a-zA-Z0-9]/g, " ")
        .replace(/\s+$/, "");
    } else {
      citationFilename = `Citation ${index}`;
    }
    return citationFilename;
  };

  // Capture each rendered chart of this message as a PNG data URL, so exports
  // contain the charts exactly as displayed in the chat.
  const captureChartImages = async (): Promise<{ image: string }[]> => {
    const images: { image: string }[] = [];
    for (let i = 0; i < parsedAnswer.charts.length; i++) {
      const node = document.getElementById(`msg-${index}-chart-${i}`);
      if (!node) continue;
      try {
        const url = await toPng(node, {
          pixelRatio: 2,
          backgroundColor: "#ffffff",
        });
        images.push({ image: url });
      } catch (e) {
        console.warn("Could not capture chart image", e);
      }
    }
    return images;
  };

  const handleExportMessage = async (format: "docx" | "pptx") => {
    setIsExporting(true);
    try {
      const response = await fetch(`/api/export/message/${format}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: parsedAnswer.markdownFormatText,
          charts: await captureChartImages(),
          tables: parsedAnswer.tables.map((t) => ({
            headers: t.headers,
            rows: t.rows,
          })),
        }),
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        format === "pptx"
          ? `slide_export_${index}.pptx`
          : `message_export_${index}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(
        `Could not export message to ${format.toUpperCase()}. Please try again.`,
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <MyStackComponent
        className={
          isFinalAnswer
            ? styles.answerContainer
            : styles.answerContainerStreaming
        }
        id={messageBoxId}
        ref={answerContainerRef}
      >
        <div style={{ alignSelf: "stretch", minWidth: 0 }}>
          <div className={styles.answerText}>
            <ReactMarkdown
              rehypePlugins={[[rehypeExternalLinks, { target: "_blank", rel: ["noopener", "noreferrer"] }]]}
              remarkPlugins={[remarkGfm, supersub]}
              children={parsedAnswer.markdownFormatText}
            />
          </div>
          {parsedAnswer.tables.map((table, i) => (
            <TableRenderer key={i} table={table} />
          ))}
          {parsedAnswer.charts.map((chart, i) => (
            <ChartRenderer key={i} chart={chart} captureId={`msg-${index}-chart-${i}`} />
          ))}
          {parsedAnswer.dashboardLinks.map((dl, i) => (
            <div key={`dl-${i}`} style={{ marginTop: 8 }}>
              <a href={dl.url} target="_blank" rel="noopener noreferrer">
                {dl.label} ↗
              </a>
            </div>
          ))}
        </div>
        <Stack horizontal className={styles.answerFooter} verticalAlign="start">
          <Stack.Item className={styles.answerDisclaimerContainer}>
            <span
              className={`${styles.answerDisclaimer} ${styles.mobileAnswerDisclaimer}`}
            >
              AI-generated content may be incorrect
            </span>
          </Stack.Item>
          {!!parsedAnswer.citations.length && (
            <Stack.Item aria-label="References">
              <Stack style={{ width: "100%" }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center"
                  tabIndex={0}
                  onClick={handleChevronClick}
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? handleChevronClick()
                      : null
                  }
                >
                  <Text className={styles.accordionTitle}>
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + " references"
                        : "1 reference"}
                    </span>
                  </Text>
                  {chevronIsExpanded ? (
                    <ExpandMore
                      className={styles.accordionIcon}
                      aria-hidden={false}
                    />
                  ) : (
                    <ChevronRight
                      className={styles.accordionIcon}
                      aria-hidden={false}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div
            ref={refContainer}
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: "4px",
              maxWidth: "100%",
            }}
          >
            {parsedAnswer.citations.map((citation, idx) => {
              return (
                <span
                  role="button"
                  onKeyDown={(e) =>
                    e.key === " " || e.key === "Enter"
                      ? onCitationClicked(citation)
                      : () => {}
                  }
                  tabIndex={0}
                  title={createCitationFilepath(citation, ++idx)}
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  className={styles.citationContainer}
                >
                  <div className={styles.citation} key={idx}>
                    {idx}
                  </div>
                  {createCitationFilepath(citation, idx, true)}
                </span>
              );
            })}
          </div>
        )}

        {isFinalAnswer && showActions && (
          <div className={styles.actionButtons}>
            <IconButton
              icons={INFO_ICONS}
              onClick={() => onIntentClicked(parsedAnswer.intent)}
              label="Show AI thinking"
            />
            {isExporting ? (
              <Spinner
                size={SpinnerSize.small}
                style={{ width: 40, height: 40 }}
              />
            ) : (
              <>
                <IconButton
                  icons={PPT_ICONS}
                  onClick={() => handleExportMessage("pptx")}
                  label="Export message as PowerPoint"
                />
                <IconButton
                  icons={WORD_ICONS}
                  onClick={() => handleExportMessage("docx")}
                  label="Export message as Word"
                />
              </>
            )}
          </div>
        )}

        <Stack.Item></Stack.Item>
      </MyStackComponent>
    </>
  );
};
